import {
    Box,
    Button,
    Icon,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import { CsvBuilder } from 'filefy';
import { Grid } from '@material-ui/core';
import { MTableSelectedActions } from '../mtable/MTableSeletedActions';
import { MTableTitle } from '../mtable/MTableTitle';
import MaterialReactTable from 'material-react-table';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SearchIcon from '@mui/icons-material/Search';
import autoTable from 'jspdf-autotable';
import classNames from 'classnames';
import jsPDF from 'jspdf';
import { makeStyles } from '@material-ui/styles';
import { styled } from '@mui/system';
import { uniq } from 'lodash';

const SearchContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
});

const SearchInput = styled('input')({
    border: 'none',
    outline: 'none',
    flex: 1,
    padding: '8px',
    fontSize: '16px',
});

const ClearButton = styled(Button)({
    minWidth: '40px',
    marginLeft: '8px',
});

const useStyles = makeStyles(theme => ({
    highlight: {
        backgroundColor: '#e3f2fd',
        color: theme.palette.secondary.main,
        height: 64,
    },
    selectedActions: {
        marginLeft: theme.spacing(2),
    },
    tableHeader: {
        '&:first-child': {
            paddingLeft: 50,
        },
    },
    header: {
        borderTop: `1px solid ${theme.palette.divider}`,
        '& > span > span': {
            width: 24,
            height: 24,
            margin: 0,
        },
    },
    toolbar: {
        '& > div:first-child, & > div:nth-child(2)': {
            flex: 'auto',
        },
    },
}));

const getMaxPath = renderData => {
    let maxPath = 0;
    renderData.forEach(item => {
        maxPath = Math.max(maxPath, calculateDepth(item));
    });
    return maxPath;
};

const calculateDepth = obj => {
    var depth = 0;
    if (obj.children) {
        obj.children.forEach(d => {
            var tmpDepth = calculateDepth(d);
            if (tmpDepth > depth) {
                depth = tmpDepth;
            }
        });
    }
    return 1 + depth;
};

const selStyles = depth =>
    makeStyles(theme => {
        const styles = {};
        for (let i = 0; i < depth; ++i) {
            styles[`row-${i}`] = {
                '& td:nth-child(3) > div': {
                    marginLeft: `-${(depth - i - 1) * 42}px`,
                },
            };
        }
        styles[`header-${depth}`] = {
            '&:nth-child(3) > span': {
                marginLeft: `-${(depth - 1) * 42}px`,
            },
        };
        return styles;
    });

const getNestedValue = (object, path) => {
    return path.split('.').reduce((o, i) => o[i], object);
};

const mergeFilters = filters => {
    const newFilters = {};
    filters.forEach(filter => {
        const filtersCopy = Object.assign({}, filter);
        if (!newFilters[filtersCopy.type]) {
            filtersCopy.value = [filtersCopy.value];
            newFilters[filtersCopy.type] = filtersCopy;
        } else {
            newFilters[filtersCopy.type].value.push(filtersCopy.value);
        }
    });

    return Object.values(newFilters);
};

const compareFilter = (filter, value) => {
    const founded = filter.value.findIndex(val => {
        switch (typeof value) {
            case 'number':
                return value === Number(val);
            case 'string':
                return value.toLowerCase().includes(val.toLowerCase());
            case 'boolean':
                return value === val;
            default:
                return true;
        }
    });

    return founded > -1;
};

const filterData = (filters, data, columns, search = '') => {
    filters = mergeFilters(filters);
    const flattenArray = filters.length || search ? flattenData(data) : data;
    const returnData = flattenArray.filter(item => {
        return filters.every(filter => {
            const column = columns.find(
                column => column.accessorKey === filter.type
            );
            const value = getNestedValue(
                item,
                (column && column.filteredBy) || filter.type
            );
            if (column.filter) {
                return column.filter(value, filter);
            }

            return compareFilter(filter, value);
        });
    });
    return returnData;
};

const flattenData = data => {
    return data.flatMap(item => {
        const flattenedItem = [item];
        if (item.children) {
            flattenedItem.push(...flattenData(item.children));
        }
        return flattenedItem;
    });
};

const VirtualizedTable = forwardRef(
    (
        {
            primaryFilters,
            primaryFilterDefault,
            onFiltersChange,
            loading,
            validating,
            data,
            columns,
            classes = {},
            actions,
            options = {},
            components = {},
            onSelectionChange,
            onSearchSubmit = () => {},
            useCustomSearch,
            searchValue,
            actionMenuItems = [],
            addMenuItems,
            canChange = false,
            detailPanel,
            onRowClick,
            searchPlaceholder,
            title = '',
            parentChildData,
            editable = {},
            stateKey,
            enableExpanding,
            height,
            sortingKey,
        },
        ref
    ) => {
        classes = { ...classes, ...useStyles() };
        const [filters, setFilters] = useState([]);
        const [globalFilter, setGlobalFilter] = useState('');
        const [anchorEl, setAnchorEl] = useState(null);
        const [rowSelection, setRowSelection] = useState({});

        useEffect(() => {
            handleSelectionChange();
        }, [rowSelection]);

        const resetSelectedRows = () => {
            setRowSelection([]);
        };

        useImperativeHandle(ref, () => ({
            resetSelectedRows,
        }));

        const handleSelectionChange = () => {
            const tableData = filterData(filters, data, columns);
            const flattenedData = flattenData(tableData);
            const values = flattenedData.filter(item =>
                Object.keys(rowSelection).includes(item.uuid)
            );
            let val = uniq(values);
            const selProps = options.selectionProps;
            if (selProps) {
                val = val.filter(row => {
                    if (typeof selProps === 'function') {
                        return !selProps(row).disabled;
                    }

                    return !selProps.disabled;
                });
            }
            if (onSelectionChange) {
                onSelectionChange(val);
            }
        };

        const handleFiltersChange = values => {
            setFilters([...values]);
            if (onFiltersChange) {
                onFiltersChange(values);
            }
        };

        const exportToCSV = () => {
            const tableData = filterData(filters, data, columns);
            const flattenedData = flattenData(tableData);
            const eData = flattenedData.map(rowData =>
                columns
                    .filter(
                        c =>
                            c.accessorKey !== 'uuid' &&
                            c.accessorKey !== 'action'
                    )
                    .map(columnDef => {
                        // Display-label hook for export. Deliberately NOT
                        // named exportValue: several columns here declare an
                        // exportValue that this table has never honoured, and
                        // honouring them now would change unrelated output.
                        if (columnDef.exportLabel) {
                            return columnDef.exportLabel(
                                rowData[columnDef.accessorKey],
                                rowData
                            );
                        }
                        if (columnDef.accessorKey === 'groups') {
                            const names = rowData[columnDef.accessorKey]
                                .map(group => group.name)
                                .join(', ');
                            return names;
                        }
                        return rowData[columnDef.accessorKey];
                    })
            );

            const builder = new CsvBuilder('export.csv');
            builder
                .setDelimeter(',')
                .setColumns(
                    columns
                        .filter(c => c.accessorKey !== 'uuid')
                        .map(columnDef => columnDef.header)
                )
                .addRows(eData)
                .exportFile();
        };

        const exportToPDF = () => {
            const tableData = filterData(filters, data, columns);
            const flattenedData = flattenData(tableData);
            const pdf = new jsPDF({
                orientation: 'landscape',
            });

            const columnHeaders = columns
                .filter(
                    c => c.accessorKey !== 'uuid' && c.accessorKey !== 'action'
                )
                .map(column => column.header);

            const rows = flattenedData.map(row => {
                const rowData = [];
                columns.forEach(column => {
                    if (
                        column.accessorKey === 'uuid' ||
                        column.accessorKey === 'action'
                    ) {
                        return;
                    }
                    if (column.exportLabel) {
                        rowData.push(
                            column.exportLabel(row[column.accessorKey], row)
                        );
                    } else if (column.accessorKey === 'groups') {
                        const names = row[column.accessorKey]
                            .map(group => group.name)
                            .join(', ');
                        rowData.push(names || '');
                    } else {
                        rowData.push(row[column.accessorKey]);
                    }
                });
                return rowData;
            });

            autoTable(pdf, {
                head: [columnHeaders],
                body: rows,
            });

            pdf.save('exported_data.pdf');
        };

        const handleClick = event => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const handleSearch = event => {
            setGlobalFilter(event.target.value);
        };

        const handleClear = () => {
            setGlobalFilter('');
        };

        let maxPath = 0;
        let rowClasses;

        return (
            <MaterialReactTable
                columns={columns}
                data={filterData(filters, data, columns, globalFilter)}
                enableColumnActions={false}
                enableExpanding={enableExpanding}
                manualExpanding={filters.length || globalFilter}
                getSubRows={originalRow => originalRow.children}
                enableRowVirtualization
                enableSelectAll
                enablePagination={false}
                enableMultiRemove={false}
                enableHiding={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableGlobalFilter
                enableFilterMatchHighlighting
                enableColumnFilters={false}
                onRowSelectionChange={setRowSelection}
                enableRowSelection={row =>
                    row.original.status !== 'creating_account'
                }
                selectAllMode="all"
                initialState={{
                    sorting: [{ id: sortingKey, desc: false }],
                    columnVisibility: { uuid: false },
                    showGlobalFilter: false,
                }}
                state={{
                    showProgressBars: validating,
                    isLoading: loading,
                    globalFilter,
                    rowSelection,
                }}
                getRowId={row => row.uuid}
                rowSelectionMode="multiple"
                muiTableContainerProps={{
                    sx: { height: `calc(100vh - ${height})` },
                }}
                muiTableHeadCellProps={{
                    style: {
                        fontFamily: 'Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: 'rgb(33,33,33)',
                    },
                }}
                muiTableBodyCellProps={({ row }) => ({
                    sx: {
                        fontSize: '0.75rem',
                        pl: '0.75rem',
                    },
                })}
                muiExpandButtonProps={({ row }) => ({
                    sx: {
                        color:
                            row.depth > 0 ? 'rgb(150,150,150) !important' : '',
                        opacity:
                            (row.depth === 0 && !row.getCanExpand()) ||
                            filters.length ||
                            globalFilter
                                ? 0
                                : 1,
                    },
                })}
                muiBottomToolbarProps={{
                    sx: {
                        height: 0,
                        minHeight: 0,
                        overflow: 'hidden',
                    },
                }}
                renderTopToolbar={({ table }) => {
                    const renderData = table.getPrePaginationRowModel().rows;
                    const selection = Object.keys(rowSelection);
                    maxPath = getMaxPath(renderData);
                    rowClasses = selStyles(maxPath);
                    return (
                        <>
                            <Grid
                                className={classNames({
                                    [classes.highlight]: selection.length > 0,
                                })}
                                container
                                alignItems="center"
                            >
                                {selection.length > 0 ? (
                                    <Grid
                                        className={classes.selectedActions}
                                        item
                                        xs="auto"
                                    >
                                        <MTableSelectedActions
                                            selected={selection}
                                            actionMenuItems={actionMenuItems}
                                        />
                                    </Grid>
                                ) : (
                                    <>
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            width="100%"
                                            height="64px"
                                            paddingLeft="24px"
                                            paddingRight="8px"
                                        >
                                            <Box flex="auto">
                                                <MTableTitle
                                                    primaryFilters={
                                                        primaryFilters
                                                    }
                                                    primaryFilterDefault={
                                                        primaryFilterDefault
                                                    }
                                                    onFiltersChange={
                                                        handleFiltersChange
                                                    }
                                                    filtersValues={filters}
                                                    useCustomSearch={
                                                        useCustomSearch
                                                    }
                                                    onSearchSubmit={() => {}}
                                                    searchValue={searchValue}
                                                    columns={columns}
                                                    addMenuItems={addMenuItems}
                                                    canChange={canChange}
                                                    data={data}
                                                    title={title}
                                                />
                                            </Box>
                                            <Box
                                                flex="1"
                                                display="flex"
                                                justifyContent="flex-end"
                                            >
                                                <SearchContainer>
                                                    <SearchIcon />
                                                    <SearchInput
                                                        type="text"
                                                        placeholder={
                                                            searchPlaceholder
                                                        }
                                                        aria-label="Search"
                                                        value={globalFilter}
                                                        onChange={handleSearch}
                                                    />
                                                    {globalFilter && (
                                                        <ClearButton
                                                            variant="text"
                                                            onClick={
                                                                handleClear
                                                            }
                                                            aria-label="Clear Search"
                                                        >
                                                            <ClearIcon />
                                                        </ClearButton>
                                                    )}
                                                </SearchContainer>
                                                <Tooltip
                                                    title="Export Data"
                                                    arrow
                                                >
                                                    <IconButton
                                                        onClick={handleClick}
                                                        style={{
                                                            backgroundColor:
                                                                'white',
                                                            borderRadius: '50%',
                                                            padding: '8px',
                                                            width: '40px',
                                                            height: '40px',
                                                            transition:
                                                                'box-shadow 0.3s ease',
                                                            marginLeft: '10px',
                                                            marginRight: '10px',
                                                        }}
                                                        onMouseEnter={e =>
                                                            (e.currentTarget.style.boxShadow =
                                                                '0 2px 5px rgba(0, 0, 0, 0.3)')
                                                        }
                                                        onMouseLeave={e =>
                                                            (e.currentTarget.style.boxShadow =
                                                                'none')
                                                        }
                                                    >
                                                        <Icon
                                                            style={{
                                                                color:
                                                                    '#757575',
                                                            }}
                                                        >
                                                            {' '}
                                                            <SaveAltIcon />
                                                        </Icon>
                                                    </IconButton>
                                                </Tooltip>
                                                <Menu
                                                    anchorEl={anchorEl}
                                                    open={Boolean(anchorEl)}
                                                    onClose={handleClose}
                                                >
                                                    <MenuItem
                                                        onClick={() => {
                                                            exportToCSV();
                                                            handleClose();
                                                        }}
                                                    >
                                                        Export to CSV
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={() => {
                                                            exportToPDF();
                                                            handleClose();
                                                        }}
                                                    >
                                                        Export to PDF
                                                    </MenuItem>
                                                </Menu>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Grid>
                        </>
                    );
                }}
            />
        );
    }
);

export default VirtualizedTable;
